import React from 'react';
import { defineMessages, useIntl, type MessageValue } from 'react-intl';
import { ScrollView, StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { DisplayText } from '@garnish/core';
import { AddressTypes } from '@garnish/location';
import { ModalRow } from '@garnish/overlays';

import { Map } from '@sg/radar-fe';

import { getEnvVars } from '@order/utils';

import { DeliveryLocationCard } from '../DeliveryLocationCard';
import { RestaurantLocationCard } from '../RestaurantLocationCard';
import { ConfirmLocationLayoutFooter } from './ConfirmLocationLayoutFooter';

import type { ConfirmLocationLayoutProps } from './ConfirmLocationLayout.types';

export const ConfirmLocationLayoutSM = (props: ConfirmLocationLayoutProps) => {
  const {
    deliveryAddress,
    isLoading,
    onContinue,
    onMoreLocations,
    orderType,
    pins,
    region,
    restaurant,
  } = props;

  const { formatMessage } = useIntl();

  const isDelivery = orderType === AddressTypes.delivery;

  return (
    <View testID="confirm-location.container-sm" style={styles.container}>
      <View style={styles.mapContainer}>
        <Map publishableKey={RADAR_SDK_KEY} region={region} pins={pins} />
      </View>

      <View style={styles.contentContainer}>
        <ScrollView contentContainerStyle={styles.scrollContentContainer}>
          <ModalRow>
            <DisplayText sizeMatch={['24']} style={styles.headline}>
              {formatMessage(messages.locationConfirmLocation, {
                channel: orderType,
              })}
            </DisplayText>

            {isDelivery && deliveryAddress ? (
              <DeliveryLocationCard deliveryAddress={deliveryAddress} />
            ) : (
              <RestaurantLocationCard orderType={orderType} restaurant={restaurant} />
            )}
          </ModalRow>
        </ScrollView>

        <ConfirmLocationLayoutFooter
          isLoading={isLoading}
          onMoreLocations={onMoreLocations}
          onContinue={onContinue}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: 500,
    flex: 1,
    flexDirection: 'row',
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  mapContainer: {
    borderRightWidth: 1,
    borderRightColor: theme.colors.DARK_KALE,
    width: '50%',
  },
  contentContainer: {
    flex: 1,
    width: '50%',
    paddingTop: 48,
  },
  scrollContentContainer: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  headline: {
    marginBottom: theme.spacing['2'],
  },
});

// ─────────────────────────────────────────────────────────────────────────────

const { RADAR_SDK_KEY } = getEnvVars();

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly locationConfirmLocation: { readonly channel: MessageValue };
}>({
  locationConfirmLocation: {
    defaultMessage: 'Confirm {channel} location',
    description: 'Location | Confirm Location',
  },
});
